/**
 * Home alert aggregation (H3 / D5) — the "+1" that turns the greeting-row power
 * pill into an alert-aware status strip.
 *
 * A false "Power on" all-clear while a P3 demand near-miss or a P4 vacancy
 * anomaly is active is exactly the incoherence D5 targets. This resolver
 * aggregates every active condition for the persona and surfaces the
 * highest-severity one plus a count.
 *
 * PURE + ROUTE-FREE by design (cross-surface parity, `feedback_cross_surface_parity_contract`):
 * it returns the condition `kind`, its i18n `labelKey`, tone, and a
 * surface-agnostic `ref` (an outage id / anomaly account) — each surface maps
 * `kind` → its own route. Web consumes it now; mobile consumes the same resolver
 * in Phase 3 (named deferral). Storm is included as the top condition ("the strip
 * already carries it", D5) while the existing layout StormBanner keeps its
 * precedence as the primary storm surface.
 */

export type HomeAlertKind = "storm" | "outage" | "demand" | "anomaly";
export type HomeAlertTone = "critical" | "warning";

export interface HomeAlert {
  kind: HomeAlertKind;
  /** Higher = more urgent. storm 4 > outage 3 > demand 2 > anomaly 1. */
  severity: number;
  tone: HomeAlertTone;
  /** i18n key for the condition label (resolved by the consumer via t()). */
  labelKey: string;
  params?: Record<string, string | number>;
  /** Surface-agnostic target (outage id / anomaly account); consumer builds the route. */
  ref?: string | undefined;
}

export interface HomeAlertState {
  /** Highest-severity active condition, or null when all clear. */
  top: HomeAlert | null;
  /** Number of active conditions. */
  count: number;
  /** All active conditions, sorted descending by severity. */
  alerts: HomeAlert[];
}

export interface HomeAlertInputs {
  storm?: { active: boolean; name?: string | undefined };
  outage?: { active: boolean; circuitName?: string | undefined; outageId?: string | undefined };
  /** P3 demo scenario: peak demand sitting near the threshold (peak-demand card visible). */
  demandNearThreshold?: boolean;
  /** P4: managed properties currently flagged for a usage anomaly. */
  anomaly?: { count: number; accountNumber?: string | undefined };
}

// Severity ladder — safety (storm) > service loss (outage) > operational/cost
// risk (demand) > informational investigation (anomaly). Mission-ordered; the
// monotonic invariant is pinned by home-alerts.test.ts (fail-red on reorder).
const SEVERITY = { storm: 4, outage: 3, demand: 2, anomaly: 1 } as const;

/** Aggregate the persona's active home conditions into a ranked alert state. */
export function resolveHomeAlerts(input: HomeAlertInputs): HomeAlertState {
  const alerts: HomeAlert[] = [];

  if (input.storm?.active) {
    alerts.push({
      kind: "storm",
      severity: SEVERITY.storm,
      tone: "critical",
      labelKey: "home.alert.storm",
      params: { name: input.storm.name ?? "" },
    });
  }
  if (input.outage?.active) {
    alerts.push({
      kind: "outage",
      severity: SEVERITY.outage,
      tone: "critical",
      labelKey: "home.outageIn",
      params: { circuit: input.outage.circuitName ?? "" },
      ref: input.outage.outageId,
    });
  }
  if (input.demandNearThreshold) {
    alerts.push({
      kind: "demand",
      severity: SEVERITY.demand,
      tone: "warning",
      labelKey: "home.alert.demand",
    });
  }
  if ((input.anomaly?.count ?? 0) > 0) {
    alerts.push({
      kind: "anomaly",
      severity: SEVERITY.anomaly,
      tone: "warning",
      labelKey: "home.alert.anomaly",
      ref: input.anomaly?.accountNumber,
    });
  }

  alerts.sort((a, b) => b.severity - a.severity);
  return { top: alerts[0] ?? null, count: alerts.length, alerts };
}
